<template>
  <ParticleAnim class="resume-section-wrapper" :class="{ 'theme--light': !$vuetify.theme.dark }">
    <v-container class="resume-container py-10">
    <!-- Profile Section with responsive sizing -->
    <v-row>
      <v-col cols="12" class="text-center profile-section">
        <v-avatar :size="$vuetify.breakpoint.smAndDown ? 150 : 250" class="profile-avatar">
          <img src="@/assets/cboy2.png" alt="profile picture" class="profile-image">
        </v-avatar>

        <h2 class="my-3 profile-name typing-animation poppin">{{ fullName }}</h2>
        <h4 class="mb-3 profile-title">{{ title }}</h4>

        <v-divider class="profile-divider" :style="{
          borderColor: $vuetify.theme.themes[$vuetify.theme.dark ? 'dark' : 'light'].primary,
          borderWidth: '2px',
          width: $vuetify.breakpoint.smAndDown ? '80%' : '50%',
          borderRadius: '8px',
          margin: '0 auto 24px'
        }"></v-divider>

        <p class="profile-summary">{{ profileSummary }}</p>
      </v-col>
    </v-row>

    <!-- Professional Experience Section Component -->
    <ProfessionalExperienceSection :experience="experience" />

    <!-- Technical Skills Section Component -->
    <TechnicalSkillsSection :skills="skills" />

    <!-- Education Section Component -->
    <EducationSection :education="education" />

    <!-- Download Resume Section -->
    <section class="download-section">
      <v-btn color="primary" @click="downloadResume" class="resume-download-btn" elevation="4" x-large rounded>
        <v-icon left>mdi-download</v-icon>
        Download Resume
      </v-btn>
    </section>
    </v-container>
  </ParticleAnim>
</template>

<script>
import ParticleAnim from '@/components/ParticleAnim.vue';
import ProfessionalExperienceSection from '@/components/subcomponents/ProfessionalExperienceSection.vue';
import TechnicalSkillsSection from '@/components/subcomponents/TechnicalSkillsSection.vue';
import EducationSection from '@/components/subcomponents/EducationSection.vue';

export default {
  name: "ResumeComponent",
  components: {
    ParticleAnim,
    ProfessionalExperienceSection,
    TechnicalSkillsSection,
    EducationSection
  },
  data() {
    return {
      fullName: "Thomas Antwi",
      title: "Software Engineer",

      profileSummary:
        "Versatile Software Engineer with a strong foundation in designing, developing, and maintaining scalable applications. Skilled in front-end frameworks like Vue and React, as well as back-end technologies such as Node.js and Python. Dedicated to delivering high-quality, user-focused solutions that meet business needs.",
      experience: [
        {
          title: "Lead Frontend Developer",
          company: "KL_Tech",
          period: "Jan 2023 - Present",
          description: "Leading frontend development initiatives and mentoring junior developers while delivering high-quality, scalable web applications.",
          responsibilities: [
            "Led the frontend team in building large-scale web applications with modern frameworks",
            "Collaborated with backend teams to create seamless user experiences and efficient APIs",
            "Improved application performance by 25% through code optimization and best practices",
            "Implemented responsive design patterns and accessibility standards across all projects",
            "Mentored junior developers and conducted code reviews to maintain high code quality"
          ],
          skills: ['Vue.js', 'React', 'TypeScript', 'Node.js', 'Leadership', 'Agile'],
          achievements: [
            { icon: 'mdi-trending-up', color: '#4caf50', text: '25% performance improvement' },
            { icon: 'mdi-account-group', color: '#2196f3', text: 'Led team of 6 developers' },
            { icon: 'mdi-rocket-launch', color: '#ff9800', text: '12+ successful project launches' }
          ]
        },
        {
          title: "Web Developer",
          company: "culusTech",
          period: "Jul 2019 - Dec 2022",
          description: "Developed responsive web applications and streamlined development processes for improved efficiency.",
          responsibilities: [
            "Designed and implemented responsive web applications using Vue.js and modern JavaScript frameworks",
            "Streamlined the development process, resulting in a 15% reduction in build time",
            "Collaborated with UI/UX designers to translate mockups into interactive web experiences",
            "Implemented state management solutions and optimized application architecture"
          ],
          skills: ['Vue.js', 'JavaScript', 'HTML5', 'CSS3', 'Vuetify', 'Git'],
          achievements: [
            { icon: 'mdi-speedometer', color: '#4caf50', text: '15% build time reduction' },
            { icon: 'mdi-devices', color: '#9c27b0', text: '20+ responsive applications' },
            { icon: 'mdi-code-tags', color: '#607d8b', text: 'Clean code practices' }
          ]
        },
        {
          title: "Senior Frontend Developer",
          company: "kaltech Developers",
          period: "Jul 2018 - Jun 2019",
          description: "Enhanced user experiences through dynamic interface development and performance optimization.",
          responsibilities: [
            "Led the development of dynamic user interfaces with Vue.js, enhancing user experience across multiple platforms",
            "Refactored existing codebases to improve performance and reduce load times by 15%",
            "Implemented modern frontend build tools and development workflows",
            "Conducted technical interviews and onboarded new team members"
          ],
          skills: ['Vue.js', 'JavaScript', 'Webpack', 'SASS', 'Testing'],
          achievements: [
            { icon: 'mdi-speedometer', color: '#4caf50', text: '15% load time reduction' },
            { icon: 'mdi-monitor', color: '#2196f3', text: 'Multi-platform compatibility' },
            { icon: 'mdi-account-plus', color: '#ff9800', text: 'Team expansion leadership' }
          ]
        }
      ],
      skills: [
        {
          title: "Python",
          icon: 'mdi-language-python',
          color: '#3572A5',
          level: 85,
          years: '4+',
          description: 'Versatile programming language for backend development and automation',
          projects: 12,
          category: 'languages'
        },
        {
          title: "JavaScript",
          icon: 'mdi-language-javascript',
          color: '#F7DF1E',
          level: 90,
          years: '5+',
          description: 'Dynamic programming language for modern web development',
          projects: 18,
          category: 'languages'
        },
        {
          title: "Vue",
          icon: 'mdi-vuejs',
          color: '#42B883',
          level: 95,
          years: '4+',
          description: 'Progressive JavaScript framework for building user interfaces',
          projects: 15,
          category: 'frontend'
        },
        {
          title: "React",
          icon: 'mdi-react',
          color: '#61DAFB',
          level: 80,
          years: '3+',
          description: 'Popular JavaScript library for building component-based UIs',
          projects: 10,
          category: 'frontend'
        },
        {
          title: "HTML5",
          icon: 'mdi-language-html5',
          color: '#E34F26',
          level: 95,
          years: '6+',
          description: 'Latest markup language for structuring web content',
          projects: 25,
          category: 'frontend'
        },
        {
          title: "CSS3",
          icon: 'mdi-language-css3',
          color: '#1572B6',
          level: 90,
          years: '5+',
          description: 'Modern styling language for responsive web design',
          projects: 22,
          category: 'frontend'
        },
        {
          title: "Angular",
          icon: 'mdi-angular',
          color: '#DD0031',
          level: 65,
          years: '2+',
          description: 'Comprehensive TypeScript-based web application framework',
          projects: 6,
          category: 'frontend'
        },
        {
          title: "Vuetify",
          icon: 'mdi-vuejs',
          color: '#1867C0',
          level: 88,
          years: '3+',
          description: 'Vue.js Material Design component framework',
          projects: 12,
          category: 'frontend'
        },
        {
          title: "Flask",
          icon: 'mdi-flask',
          color: '#000000',
          level: 78,
          years: '3+',
          description: 'Lightweight Python web framework for rapid development',
          projects: 8,
          category: 'backend'
        },
        {
          title: "RESTful APIs",
          icon: 'mdi-api',
          color: '#4CAF50',
          level: 85,
          years: '4+',
          description: 'Design and implementation of scalable web services',
          projects: 14,
          category: 'backend'
        },
        {
          title: "C++",
          icon: 'mdi-language-cpp',
          color: '#00599C',
          level: 75,
          years: '3+',
          description: 'High-performance programming for system-level applications',
          projects: 7,
          category: 'languages'
        },
        {
          title: "Java",
          icon: 'mdi-language-java',
          color: '#007396',
          level: 70,
          years: '2+',
          description: 'Enterprise-grade object-oriented programming language',
          projects: 5,
          category: 'languages'
        },
        {
          title: "Git",
          icon: 'mdi-git',
          color: '#F05032',
          level: 85,
          years: '4+',
          description: 'Distributed version control system for code management',
          projects: 30,
          category: 'tools'
        },
        {
          title: "Agile",
          icon: 'mdi-tune',
          color: '#6DB33F',
          level: 82,
          years: '3+',
          description: 'Iterative project management and software development methodology',
          projects: 20,
          category: 'tools'
        },
        {
          title: "C#",
          icon: 'mdi-language-csharp',
          color: '#178600',
          level: 72,
          years: '2+',
          description: 'Microsoft\'s versatile programming language for various applications',
          projects: 4,
          category: 'languages'
        }
      ],
      education: [
        {
          institution: "[OKESS] Osei Kyeretwie Senior High School - Tafo",
          degree: "General Science with IT",
          period: "2018 - 2021",
          description: "Specialized in IT-related topics with a strong foundation in mathematics, science, and computer fundamentals. Developed early programming skills and technical problem-solving abilities.",
          location: "Tafo, Ghana",
          status: "completed",
          courses: [
            "Mathematics",
            "Physics",
            "Chemistry",
            "Computer Science",
            "Information Technology",
            "Statistics",
            "Technical Drawing"
          ],
          achievements: [
            { icon: 'mdi-trophy', color: '#ffd700', text: 'Best Student in IT' },
            { icon: 'mdi-account-group', color: '#2196f3', text: 'Science Club President' },
            { icon: 'mdi-code-tags', color: '#4caf50', text: 'First programming project' }
          ],
          skills: ['Problem Solving', 'Analytical Thinking', 'IT Fundamentals', 'Mathematics']
        },
        {
          institution: "University of Mines and Technology - Tarkwa",
          degree: "Bachelor of Computer Science and Engineering",
          period: "2022 - 2026",
          description: "Pursuing a comprehensive degree in Computer Science and Engineering with specialization in blockchain technology, web development, and mobile app development. Engaging in practical projects and industry collaborations.",
          location: "Tarkwa, Ghana",
          status: "in-progress",
          progress: 75,
          website: "https://umat.edu.gh",
          courses: [
            "Data Structures & Algorithms",
            "Web Development",
            "Mobile App Development",
            "Blockchain Technology",
            "Database Systems",
            "Software Engineering",
            "Computer Networks",
            "Machine Learning",
            "System Design",
            "Operating Systems",
            "Software Architecture",
            "Cybersecurity",
            "Cloud Computing"
          ],
          achievements: [
            { icon: 'mdi-trending-up', color: '#4caf50', text: 'Consistent Dean\'s List' },
            { icon: 'mdi-lightbulb', color: '#ff9800', text: 'Innovation Award Winner' },
            { icon: 'mdi-account-group', color: '#2196f3', text: 'Tech Society Leader' },
            { icon: 'mdi-rocket-launch', color: '#9c27b0', text: 'Multiple project launches' }
          ],
          skills: [
            'Full-Stack Development',
            'Blockchain Development',
            'Mobile Development',
            'System Architecture',
            'Project Management',
            'Team Leadership'
          ]
        }
      ],
    };
  },
  methods: {
    async downloadResume() {
      try {
        const resumeUrl = '/kalculusResume.pdf'; // path to your resume
        const response = await fetch(resumeUrl);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const link = document.createElement('a');
        link.href = resumeUrl;
        link.setAttribute('download', 'kalculusResume.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.$store.commit('setSnackbar', {
          snackbarSuccess: true,
          snackbar: true,
          snackbarMsg: "Resume downloading",
        });
      } catch (error) {
        console.error("Error downloading resume:", error);
        this.$store.commit('setSnackbar', {
          snackbarSuccess: false,
          snackbar: true,
          snackbarMsg: "Failed to download resume",
        });
      }
    },
  },
};
</script>

<style scoped>
@import "@/styles/variables.scss";

/* Theme-aware background wrapper - overrides particles */
.resume-section-wrapper {
  position: relative;
  overflow: hidden;
  min-height: 100vh;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      135deg,
      #0a0a0a 0%,
      #1a1a2e 50%,
      #16213e 100%
    );
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(67, 97, 238, 0.05), rgba(72, 149, 239, 0.05));
    z-index: 1;
  }

  /* Light theme styling */
  &.theme--light {
    &::before {
      background: linear-gradient(
        135deg,
        #f8f9fa 0%,
        #e9ecef 50%,
        #dee2e6 100%
      );
    }

    &::after {
      background: linear-gradient(135deg, rgba(67, 97, 238, 0.03), rgba(72, 149, 239, 0.03));
    }
  }

  .v-container {
    position: relative;
    z-index: 2;
  }
}

.resume-container {
  max-width: 90%;
  margin: 0 auto;
  padding: 1rem;
  position: relative;
  z-index: 1;
}

/* Profile Section Styles */
.profile-section {
  margin-bottom: 4rem;
  transition: all 0.3s ease;
  animation: fadeInUp 1s ease-out;
}

.profile-avatar {
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 3px solid var(--v-primary-base);
  transition: all 0.3s ease;
  animation: pulse 2s infinite;
}

.profile-avatar:hover {
  transform: scale(1.05);
}

.profile-image {
  transition: all 0.3s ease;
}

.profile-name {
  font-size: clamp(1.8rem, 5vw, 2.5rem);
  font-weight: 700;
  color: var(--v-textPrimary-base);
  margin-bottom: 0.5rem;
  transition: all 0.3s ease;
}

.profile-title {
  font-size: clamp(1rem, 3vw, 1.25rem);
  font-weight: 500;
  color: var(--v-textSecondary-base);
  margin-bottom: 1rem;
  transition: all 0.3s ease;
}

.profile-summary {
  font-size: clamp(0.9rem, 2vw, 1.1rem);
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
  color: var(--v-textSecondary-base);
  transition: all 0.3s ease;
  padding: 0 1rem;
}

/* Download Section */
.download-section {
  text-align: center;
  margin: 4rem 0;
}

.resume-download-btn {
  font-size: clamp(0.9rem, 3vw, 1.1rem);
  font-weight: 600;
  padding: 0.75rem 2rem;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.resume-download-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: 0.5s;
}

.resume-download-btn:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15) !important;
}

.resume-download-btn:hover::before {
  left: 100%;
}

/* Dark Mode Adjustments for Profile */
.theme--dark .profile-summary {
  color: rgba(255, 255, 255, 0.8);
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(var(--v-primary-base), 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(var(--v-primary-base), 0); }
  100% { box-shadow: 0 0 0 0 rgba(var(--v-primary-base), 0); }
}

/* Responsive Adjustments */
@media (max-width: 600px) {
  .resume-container {
    max-width: 100%;
    padding: 0.5rem;
  }

  .download-section {
    margin: 3rem 0;
  }

  .resume-download-btn {
    padding: 0.6rem 1.5rem;
  }
}
</style>
